import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  DailyPositionReportRecord,
  DprMovementReportRow,
  blankMovementReportRow,
  blankDpr
} from '../core/_dprModels'
import { DmrMovementReportRow } from '../core/_dmrModels'

interface AddMovementReportModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (dpr: DailyPositionReportRecord) => void
  selectedVessel: string
  reportDate: string
  existingRows: DmrMovementReportRow[]
  dmrList: any[]
}

export const AddMovementReportModal: React.FC<AddMovementReportModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedVessel,
  reportDate: pageReportDate,
  existingRows,
  dmrList,
}) => {
  const { currentUser } = useAuth()
  const currentVesselId = Number((currentUser as any)?.vessel?.id ?? 0)

  const [reportDate, setReportDate] = useState<string>('')
  const [movementReport, setMovementReport] = useState<DprMovementReportRow>(blankMovementReportRow())

  useEffect(() => {
    if (isOpen) {
      setReportDate(pageReportDate || new Date().toISOString().split('T')[0])
      setMovementReport(blankMovementReportRow())
    }
  }, [isOpen, pageReportDate])

  const parseMovementTime = (t: string): number | null => {
    let m = t.match(/^(\d{1,2}):(\d{2})$/)
    if (m) return parseInt(m[1]) * 60 + parseInt(m[2])
    m = t.match(/^(\d{2})(\d{2})$/)
    if (m) return parseInt(m[1]) * 60 + parseInt(m[2])
    return null
  }

  const isValidMovementTime = (v: string): boolean => {
    const t = v.trim()
    if (!t || t === '-' || t.toLowerCase() === 'continue') return false
    return parseMovementTime(t) !== null
  }

  /**
   * Find the start time of the currently open (unclosed) movement.
   * 
   * Scans all rows in chronological order (historical DMRs first, then
   * current session rows). Tracks the open movement state:
   * - A row with Finished M.E. = "Continue" OPENS a movement (saves its start time)
   * - A row with a valid Finished M.E. time CLOSES any open movement
   * 
   * Returns the start time of the open movement if one exists, or null.
   */
  const findOpenMovementStartTime = (): string | null => {
    let openStartTime: string | null = null

    // 1. Scan historical DMR rows in date-ascending order
    const sortedDmrList = [...(dmrList || [])].sort((a, b) => {
      return (a.reportDate || '').localeCompare(b.reportDate || '')
    })

    for (const dmr of sortedDmrList) {
      const rows = dmr.movementReportRows || []
      for (const row of rows) {
        const fin = (row.alongSideFinishedME || '').trim().toLowerCase()
        if (fin === 'continue') {
          // This row opens a movement — save its start time
          const st = (row.startedBothME || '').trim()
          if (isValidMovementTime(st)) {
            openStartTime = st
          }
        } else if (isValidMovementTime((row.alongSideFinishedME || '').trim())) {
          // This row has a valid finish time — it closes any open movement
          openStartTime = null
        }
      }
    }

    // 2. Scan current session rows (in insertion order)
    for (const row of existingRows) {
      const fin = (row.alongSideFinishedME || '').trim().toLowerCase()
      if (fin === 'continue') {
        const st = (row.startedBothME || '').trim()
        if (isValidMovementTime(st)) {
          openStartTime = st
        }
      } else if (isValidMovementTime((row.alongSideFinishedME || '').trim())) {
        // Closed by this row
        openStartTime = null
      }
    }

    return openStartTime
  }

  const recalcSingleMovementTotal = (
    row: DprMovementReportRow,
  ): DprMovementReportRow => {
    const fin = row.alongSideFinishedME.trim()

    // Rule: If finished ME is "Continue", leave totalRHrs blank (open movement)
    if (!fin || fin.toLowerCase() === 'continue') {
      return { ...row, totalRHrs: '' }
    }

    const finishMin = parseMovementTime(fin)
    if (finishMin === null) return { ...row, totalRHrs: '' }

    // Check if there is an open (unclosed) movement from a previous row
    const openStart = findOpenMovementStartTime()

    let startTime: string

    if (openStart) {
      // An open movement exists — use its original start time
      // (ignore the current row's Started Both M.E. for calculation)
      startTime = openStart
    } else {
      // No open movement — use current row's Started Both M.E.
      startTime = row.startedBothME.trim()

      // Fallback logic for blank start time
      if (!isValidMovementTime(startTime)) {
        const fallbacks = [row.cOff, row.makeFastPushStart, row.lineCastOffPushStop, row.pilotRelease]
        for (const fb of fallbacks) {
          if (isValidMovementTime(fb)) { startTime = fb.trim(); break }
        }
      }
    }

    if (!isValidMovementTime(startTime)) return { ...row, totalRHrs: '' }
    
    const startMin = parseMovementTime(startTime)!
    let diff = finishMin - startMin
    if (diff < 0) diff += 24 * 60 // midnight crossing
    
    return { ...row, totalRHrs: (diff / 60).toFixed(2) }
  }


  const MovementTimeCell = ({ value, onChange }: { value: string; onChange: (val: string) => void }) => {
    const isContinue = value.toLowerCase() === 'continue'

    const handleTimeChange = (newVal: string) => {
      onChange(newVal)
    }

    if (isContinue) {
      return (
        <div className='d-flex align-items-center gap-1 justify-content-center'>
          <span className='badge bg-info text-white' style={{ fontSize: '11px', padding: '5px 8px' }}>Continue</span>
          <button
            type='button'
            className='btn btn-sm btn-outline-secondary py-0 px-1'
            onClick={() => onChange('')}
            title='Switch to time picker'
            style={{ fontSize: '11px', lineHeight: '1.2' }}
          >
            ✕
          </button>
        </div>
      )
    }

    return (
      <div>
        <div className='d-flex align-items-center gap-1'>
          <input
            type='time'
            className='form-control form-control-sm text-center'
            value={value}
            onChange={(e) => handleTimeChange(e.target.value)}
            style={{ minWidth: '90px' }}
          />
          <button
            type='button'
            className='btn btn-sm btn-outline-info py-0 px-1'
            onClick={() => onChange('Continue')}
            title='Set as Continue (engine still running)'
            style={{ fontSize: '10px', lineHeight: '1.2', whiteSpace: 'nowrap' }}
          >
            C
          </button>
        </div>
      </div>
    )
  }

  const handleSave = () => {
    if (!selectedVessel) {
      toast.warning('Please select a vessel from Vessel Status tab.')
      return
    }
    if (!reportDate) {
      toast.warning('Please select a date.')
      return
    }

    const payload: DailyPositionReportRecord = {
      ...blankDpr(),
      vesselId: currentVesselId || 0,
      vesselName: selectedVessel,
      reportDate: reportDate,
      movementReport: movementReport,
      status: 'SUBMITTED' // directly submit it
    }

    onSave(payload)
  }

  if (!isOpen) return null

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered dialogClassName='custom-wide-modal' backdrop='static'>
      <style>{`
        .custom-wide-modal { max-width: 95vw !important; }
      `}</style>
      <div className='modal-header'>
        <h2 className='fw-bolder'>Add Movement Report</h2>
        <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={onClose}>
          <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
        </div>
      </div>
      
      <div className='modal-body scroll-y mx-5 mx-xl-15 my-7'>
            <div className='row mb-8'>
              <div className='col-md-6'>
                <label className='form-label fw-bolder required'>Vessel</label>
                <input type='text' className='form-control form-control-solid bg-light' value={selectedVessel || 'No vessel selected'} readOnly />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-bolder required'>Report Date</label>
                <input 
                  type='date' 
                  className='form-control form-control-solid' 
                  value={reportDate} 
                  onChange={e => setReportDate(e.target.value)} 
                />
              </div>
            </div>

            <div className='table-responsive'>
              <table className='table table-bordered table-sm align-middle text-center mb-0'>
                <thead className='table-light'>
                  <tr>
                    <th style={{ minWidth: '120px' }}>BER/U.BER/SHIFTING</th>
                    <th style={{ minWidth: '90px' }}>PILOT NAME</th>
                    <th style={{ minWidth: '100px' }}>STARTED BOTH M.E.</th>
                    <th style={{ minWidth: '90px' }}>C/OFF</th>
                    <th style={{ minWidth: '110px' }}>MAKE FAST /PUSH START</th>
                    <th style={{ minWidth: '120px' }}>LINE CAST OFF/PUSH STOP</th>
                    <th style={{ minWidth: '100px' }}>PILOT RELEASE</th>
                    <th style={{ minWidth: '120px' }}>ALONG SIDE/ FINISHED M.E.</th>
                    <th style={{ minWidth: '80px' }}>TOTAL R/HRS</th>
                    <th style={{ minWidth: '140px' }}>OTHER TUG</th>
                    <th style={{ minWidth: '140px' }}>REMARKS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <input
                        type='text'
                        className='form-control form-control-sm text-center'
                        value={movementReport.berShifting}
                        onChange={(e) => setMovementReport({ ...movementReport, berShifting: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type='text'
                        className='form-control form-control-sm text-center'
                        value={movementReport.pilotName}
                        onChange={(e) => setMovementReport({ ...movementReport, pilotName: e.target.value })}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.startedBothME}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, startedBothME: newVal }))}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.cOff}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, cOff: newVal }))}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.makeFastPushStart}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, makeFastPushStart: newVal }))}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.lineCastOffPushStop}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, lineCastOffPushStop: newVal }))}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.pilotRelease}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, pilotRelease: newVal }))}
                      />
                    </td>
                    <td>
                      <MovementTimeCell
                        value={movementReport.alongSideFinishedME}
                        onChange={(newVal) => setMovementReport(recalcSingleMovementTotal({ ...movementReport, alongSideFinishedME: newVal }))}
                      />
                    </td>
                    <td className='bg-light fw-bold fs-6'>
                      {movementReport.totalRHrs || '0.00'}
                    </td>
                    <td>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={movementReport.otherTug}
                        onChange={(e) => setMovementReport({ ...movementReport, otherTug: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={movementReport.remarks}
                        onChange={(e) => setMovementReport({ ...movementReport, remarks: e.target.value })}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div className='modal-footer text-center'>
            <button type='reset' className='btn btn-light me-3' onClick={onClose}>
              Cancel
            </button>
            <button type='button' className='btn btn-primary' onClick={handleSave}>
              <span className='indicator-label'>Add Details</span>
            </button>
          </div>
    </Modal>
  )
}

export default AddMovementReportModal
